import * as yup from 'yup';
import { Formik } from 'formik';
import { Box } from '@mui/material';
import { keys, length } from 'ramda';
import { TextField } from '@qrios/frontend-library';

import {
  nameValidation,
  emailValidation,
  countryValidation,
  callbackValidation,
  msisdnValidationSimpleRequired
} from '../../../../../../../../../constants/validation';
import locales from '../../../../../../../../../constants/locales';
import WithFormLayout from '../../../../../../../../common/with-form-layout';
import { diffObject } from '../../../../../../../../../constants/helpers/diff';
import { BusinessError } from '../../../../../../../../common/business-error';
import { ACCOUNT_INFORMATION } from '../../../../../../../../../constants/formInputs';
import {
  AlertBeforeOtpSendInEdit,
  InfoWhenActionEndWithSuccess
} from '../../../../../../../../common/common-alert-messages';

import { AccountInfo } from '../interfaces';

const validationSchema = yup.object().shape({
  [ACCOUNT_INFORMATION.EMAIL]: emailValidation,
  [ACCOUNT_INFORMATION.NAME]: nameValidation,
  [ACCOUNT_INFORMATION.COUNTRY]: countryValidation,
  [ACCOUNT_INFORMATION.MSISDN]: msisdnValidationSimpleRequired,
  [ACCOUNT_INFORMATION.CALLBACK]: callbackValidation
});

interface Props {
  initialValues: AccountInfo;
  userEditedSuccessfully?: boolean;
  onSubmit: (props: Partial<AccountInfo>) => void;
  handleSecondary: () => void;
  loading: boolean;
  error?: BusinessError | null;
}

const EditAccountForm = ({
  initialValues,
  onSubmit,
  handleSecondary,
  loading,
  error = null,
  userEditedSuccessfully
}: Props) => (
  <Formik
    initialValues={{
      [ACCOUNT_INFORMATION.EMAIL]: initialValues.emailAddress,
      [ACCOUNT_INFORMATION.NAME]: initialValues.name,
      [ACCOUNT_INFORMATION.COUNTRY]: initialValues.country,
      [ACCOUNT_INFORMATION.MSISDN]: initialValues.msisdn,
      [ACCOUNT_INFORMATION.CALLBACK]: initialValues.callback
    }}
    validationSchema={validationSchema}
    onSubmit={values => {
      const diff = diffObject({ ...initialValues, msisdn: initialValues.msisdn }, { ...values, msisdn: values.msisdn });
      onSubmit(diff);
    }}
  >
    {({ values, handleChange, handleSubmit, errors, touched, handleBlur }) => {
      const valuesUnchanged =
        length(
          keys(diffObject({ ...initialValues, msisdn: initialValues.msisdn }, { ...values, msisdn: values.msisdn }))
        ) === 0;
      const msisdnUnchanged =
        length(keys(diffObject({ msisdn: initialValues.msisdn }, { msisdn: values.msisdn }))) === 0;

      return (
        <>
          {userEditedSuccessfully && valuesUnchanged && (
            <Box mb={3}>
              <InfoWhenActionEndWithSuccess />
            </Box>
          )}
          <WithFormLayout
            formInfoText="Edit your account information"
            submitLabel={msisdnUnchanged ? 'Update' : 'Apply and go to OTP verification'}
            secondaryLabel="Refresh"
            onSubmit={handleSubmit}
            onSecondary={handleSecondary}
            loading={loading}
            error={error}
            disableSubmit={valuesUnchanged || (userEditedSuccessfully && valuesUnchanged)}
          >
            <Box mb={1}>
              <TextField
                disabled
                name={ACCOUNT_INFORMATION.EMAIL}
                value={values[ACCOUNT_INFORMATION.EMAIL]}
                handleChange={handleChange}
                handleBlur={handleBlur}
                label={locales.accountInformation.label.email}
                error={errors[ACCOUNT_INFORMATION.EMAIL]}
                touched={touched[ACCOUNT_INFORMATION.EMAIL]}
                placeholder={locales.accountInformation.placeholder.email}
                helpInfo="Email address can't be changed"
              />
            </Box>
            <Box mb={1}>
              <TextField
                data-testid="input-name"
                name={ACCOUNT_INFORMATION.NAME}
                value={values[ACCOUNT_INFORMATION.NAME]}
                handleChange={handleChange}
                handleBlur={handleBlur}
                label={locales.accountInformation.label.name}
                error={errors[ACCOUNT_INFORMATION.NAME]}
                touched={touched[ACCOUNT_INFORMATION.NAME]}
                placeholder={locales.accountInformation.placeholder.name}
              />
            </Box>
            <Box mb={1}>
              <TextField
                data-testid="input-country"
                name={ACCOUNT_INFORMATION.COUNTRY}
                value={values[ACCOUNT_INFORMATION.COUNTRY]}
                handleChange={handleChange}
                handleBlur={handleBlur}
                label={locales.accountInformation.label.country}
                error={errors[ACCOUNT_INFORMATION.COUNTRY]}
                touched={touched[ACCOUNT_INFORMATION.COUNTRY]}
                placeholder={locales.accountInformation.placeholder.country}
              />
            </Box>
            <Box mb={1}>
              <TextField
                data-testid="input-phone-number"
                name={ACCOUNT_INFORMATION.MSISDN}
                value={values[ACCOUNT_INFORMATION.MSISDN]}
                handleChange={handleChange}
                handleBlur={handleBlur}
                label={locales.accountInformation.label.msisdn}
                error={errors[ACCOUNT_INFORMATION.MSISDN]}
                touched={touched[ACCOUNT_INFORMATION.MSISDN]}
                placeholder={locales.accountInformation.placeholder.msisdn}
              />
            </Box>
            <Box mb={2}>
              <TextField
                data-testid="input-callback"
                name={ACCOUNT_INFORMATION.CALLBACK}
                value={values[ACCOUNT_INFORMATION.CALLBACK]}
                handleChange={handleChange}
                handleBlur={handleBlur}
                label={locales.accountInformation.label.callback}
                error={errors[ACCOUNT_INFORMATION.CALLBACK]}
                touched={touched[ACCOUNT_INFORMATION.CALLBACK]}
                placeholder={locales.accountInformation.placeholder.callbackUrl}
              />
            </Box>
            {!msisdnUnchanged && (
              <Box mb={2}>
                <AlertBeforeOtpSendInEdit />
              </Box>
            )}
          </WithFormLayout>
        </>
      );
    }}
  </Formik>
);

export default EditAccountForm;
